/**
 * Copy for the sign-in form (`LoginForm.tsx`), its error block (`Login.Components.tsx` →
 * `ErrorMessage`, which reads `validationErrorMessage` and `resetPasswordText`), the
 * create-account panel (`../CreateAccountForm.tsx`) and the registration page's account panel
 * (`components/Register/AccountForm.tsx`, which alone reads `statsBlurb`). The API error texts
 * (`AUTH_ERROR` from `@ryerson/frontend.common`, and the lock-out and reset messages in
 * `Shared/Password/`) are not here.
 */
import { getBrandInfo } from "@ryerson/frontend.common";

/** One language's sign-in copy. */
export interface LanguageText {
	emailText: string;
	passwordText: string;
	passwordShowText: string;
	passwordHideText: string;
	forgotPasswordText: string;
	resetPasswordText: string;
	loginText: string;
	needAccountText: string;
	createAccountText: string;
	accessText: string;
	saveToShoppingList: string;
	quotesText: string;
	payInvoiceText: string;
	fasterCheckout: string;
	orderRenewalText: string;
	createButtonText: string;
	rememberMyUsernameText: string;
	validationErrorMessage: string;
	statsBlurb?: StatsBlurb;
	back:string;
	continueText:string;
}

type StatsBlurb = {
	title: string;
	statBlurbText: string[];
	statSubBlurb: string[];
	statSubBlurb2: string[];
};
interface Text {
	[key: string]: LanguageText;
}

// Read once at module load; the brand is fixed per build (`NEXT_PUBLIC_COMPANY`).
const brand = getBrandInfo();

/**
 * Sign-in copy keyed by language code (`en`, `fr`, `es`). `statsBlurb` is English in all three.
 * `LoginForm.tsx` and `Login.Components.tsx` index it without an `en` fallback.
 */
export const LoginContent: Text = {
	en: {
		emailText: "Email or Username",
		passwordText: "Password",
		passwordShowText: "SHOW",
		passwordHideText: "HIDE",
		rememberMyUsernameText: "Remember my username",
		forgotPasswordText: "Forgot Password?",
		createAccountText:
			"Create an account to track your order and access industry-leading experience",
		resetPasswordText: "Reset password?",
		loginText: "Log In",
		needAccountText: "Need an Account?",
		accessText: "Access order status and tracking",
		saveToShoppingList: "Save your custom shopping lists",
		quotesText: "Create, view, and order saved quotes",
		payInvoiceText: "Pay invoices online",
		fasterCheckout: "Select your default settings for faster checkout",
		orderRenewalText: "Set up automated order renewals",
		createButtonText: "Create an Account",
		validationErrorMessage:
			"Please review the highlighted fields and make any necessary changes.",
		statsBlurb: {
			title: `You are in a good company with ${brand.name}`,
			statBlurbText: ["70,000+", "180+"],
			statSubBlurb: ["Metal", "Years"],
			statSubBlurb2: ["Products", "in Business"],
		},
		back: "Back", 
		continueText: "Continue"
	},
	fr: {
		emailText: "Courriel ou nom d’utilisateur",
		passwordText: "Mot de passe",
		passwordShowText: "RÉVÉLER",
		passwordHideText: "CACHER",
		rememberMyUsernameText: "Se souvenir de mon nom d’utilisateur",
		forgotPasswordText: "Mot de passe oublié?",
		createAccountText:
			"Créer un compte pour suivre votre commande et avoir accès à notre expérience de premier plan dans le secteur",
		resetPasswordText: "Réinitialiser votre mot de passe?",
		loginText: "Se connecter",
		needAccountText: "Besoin d’un compte?",
		accessText: "Statut et suivi de la commande",
		saveToShoppingList: "Enregistrer vos listes de courses",
		quotesText: "Créer, afficher et commander les devis enregistrés",
		payInvoiceText: "Payer les factures en ligne",
		fasterCheckout:
			"Sélectionner vos paramètres par défaut pour une validation d’achat plus rapide",
		orderRenewalText: "Configurer le renouvellement automatique de commandes",
		createButtonText: "Créer un compte",
		validationErrorMessage:
			"Veuillez vérifier les champs surlignés et apporter les modifications nécessaires.",
		statsBlurb: {
			title: `You are in a good company with ${brand.name}`,
			statBlurbText: ["70,000+", "180+"],
			statSubBlurb: ["Metal", "Years"],
			statSubBlurb2: ["Products", "in Business"],
		},
		back: "Retour",
		continueText: "Continuer"
	},
	es: {
		emailText: "Correo electrónico o nombre de usuario",
		passwordText: "Contraseña",
		passwordShowText: "REVELAR",
		passwordHideText: "ESCONDER",
		rememberMyUsernameText: "Recordar mi nombre de usuario",
		forgotPasswordText: "¿Ha olvidado su contraseña?",
		createAccountText:
			"Cree una cuenta para realizar un seguimiento de su pedido y acceder a una experiencia de primer nivel",
		resetPasswordText: "¿Reiniciar su contraseña?",
		loginText: "Iniciar sesión",
		needAccountText: "¿Necesita una cuenta?",
		accessText: "Acceda al estado y seguimiento del pedido",
		saveToShoppingList: "Guarde sus listas de compras personalizadas",
		quotesText: "Cree, consulte y solicite presupuestos guardados",
		payInvoiceText: "Pague facturas en línea",
		fasterCheckout: "Seleccione su configuración predeterminada para comprar más rápido",
		orderRenewalText: "Configure renovaciones de pedidos automatizadas",
		createButtonText: "Cree una cuenta",
		validationErrorMessage: "Revise los campos resaltados y haga los cambios necesarios.",
		statsBlurb: {
			title: `You are in a good company with ${brand.name}`,
			statBlurbText: ["70,000+", "180+"],
			statSubBlurb: ["Metal", "Years"],
			statSubBlurb2: ["Products", "in Business"],
		},
		back: "Atrás",
		continueText: "Continuar"
	},
};
